import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  Building2, Users, BookOpen, TrendingUp,
  Power, PowerOff, Plus, LayoutDashboard,
  X, Loader2, AlertCircle, UserPlus, KeyRound,
  Copy, Check, RefreshCw, Eye, EyeOff,
  Mail, CreditCard, Hash, Briefcase,
  CheckCircle2, ArrowLeft, Send, Shield,
  FileSpreadsheet, UserX, ToggleLeft, ToggleRight, Trash2,
  UserCog, Upload, Video, Clock, Award, Brain, Sparkles,
  Palette
} from 'lucide-react';
import ModalOverlay from 'components/ModalOverlay';
import { toast } from 'sonner';
import BulkUserUpload from 'components/BulkUserUpload';
import OrgBrandingEditor from 'components/OrgBrandingEditor';   
import OrgPlusFeatures from 'components/OrgPlusFeatures';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EMPTY_ORG = { 
  name: '', description: '', contact_email: '', logo_url: '', bg_image_url: '', max_users: 100,
 
};

const EMPTY_USER = { 
  name: '', dni: '', social_security: '', email: '', 
  job_title: '', department: '', role: 'user', learning_style: 'visual' 
};

/* ─── GLOBAL STYLES ────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  
  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .dash-root { 
    font-family: 'DM Sans', sans-serif; 
    min-height: 100vh;
    background-color: #060810;
    background-image: var(--brand-bg-image, url('/bg-dashboard.png'));
    background-size: cover;
    background-position: center top;
    background-attachment: fixed;
    position: relative;
    overflow-x: hidden;
    animation: fadeIn 0.4s ease;
    color: #ffffff;
  }

  .dash-root::before {
    content: ''; position: fixed; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.4), transparent);
    animation: scanLine 8s linear infinite; z-index: 1; pointer-events: none;
  }

  .main-content {
    position: relative; z-index: 2; padding: 32px 24px; max-width: 80rem; margin: 0 auto;
  }

  .glass-panel {
    background: rgba(10, 8, 30, 0.65);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 0.5px rgba(255,255,255,0.05);
  }

  .title-neon {
    color: #FFF;
    text-shadow: 0 0 15px rgba(0, 255, 255, 0.4);
    letter-spacing: -0.02em;
  }

  .glow-cyan { box-shadow: 0 0 20px rgba(0, 255, 255, 0.08); border-color: rgba(0, 255, 255, 0.2); }
  .glow-cyan:hover { box-shadow: 0 0 30px rgba(0, 255, 255, 0.15); border-color: rgba(0, 255, 255, 0.4); }
  
  .btn-neon-cyan {
    background: #00FFFF; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.4); transition: all 0.2s;
  }
  .btn-neon-cyan:hover:not(:disabled) { background: #66FFFF; box-shadow: 0 0 25px rgba(0, 255, 255, 0.6); transform: translateY(-1px); }
  .btn-neon-cyan:disabled { background: rgba(0, 255, 255, 0.1); color: rgba(0, 255, 255, 0.3); box-shadow: none; cursor: not-allowed; }

  .btn-neon-lime {
    background: #ccff00; color: #000; font-weight: 800; border: none;
    box-shadow: 0 0 15px rgba(204, 255, 0, 0.4); transition: all 0.2s;
  }
  .btn-neon-lime:hover:not(:disabled) { background: #d4ff33; box-shadow: 0 0 25px rgba(204, 255, 0, 0.6); transform: translateY(-1px); }
  .btn-neon-lime:disabled { background: rgba(204, 255, 0, 0.1); color: rgba(204, 255, 0, 0.3); box-shadow: none; cursor: not-allowed; }

  .cy-input {
    width: 100%; padding: 12px 16px; border-radius: 12px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1);
    color: #fff; font-size: 14px; transition: all 0.3s;
  }
  .cy-input:focus { border-color: #00FFFF; box-shadow: 0 0 15px rgba(0,255,255,0.2); outline: none; }
  .cy-input::placeholder { color: rgba(255,255,255,0.3); }
  .cy-input.error { border-color: #ef4444; box-shadow: 0 0 15px rgba(239,68,68,0.2); }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(0,255,255,0.4); }

  .cyber-modules-root * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

/* ── COMPONENTES SECUNDARIOS ── */

const UserRow = ({ 
  user, code, visible, loadingCode, copied, departments, 
  isToggling, isDeleting, onToggleCode, onCopy, onRegenerate, 
  onToggleStatus, onDelete, onEdit, onSendCode,
  adminCode, isPromoting, onPromote  
}) => { 
  const dept = departments.find(d => d.id === user.department);
  const isSuspended = user.status === 'rejected' || user.status === 'inactive';
  
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-4 p-5 hover:bg-white/[0.02] transition-colors border-l-2 ${isSuspended ? 'border-red-500/50 opacity-60' : 'border-transparent hover:border-[#ccff00]'}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black shrink-0 uppercase shadow-[0_0_10px_rgba(204,255,0,0.1)] ${isSuspended ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-[#ccff00]/10 border-[#ccff00]/30 text-[#ccff00]'}`}>
        {user.name?.[0] || '?'}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1.5 flex-wrap">
          <span className={`font-bold text-[15px] ${isSuspended ? 'text-white/60 line-through' : 'text-white'}`}>{user.name}</span>
          {isSuspended && <span className="text-[9px] px-2 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/30 uppercase tracking-widest font-black">Suspendido</span>}
          {!isSuspended && <span className="text-[9px] px-2 py-0.5 rounded border bg-white/10 text-white/50 border-white/20 uppercase tracking-widest font-black">Operario</span>}
          {dept && <span className="text-[9px] px-2 py-0.5 bg-[#00FFFF]/10 border border-[#00FFFF]/30 text-[#00FFFF] uppercase tracking-widest font-black rounded">{dept.name}</span>}
        </div>
        
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] font-bold text-white/40 uppercase tracking-wider">
          {user.email && !user.email.includes('@noemail.local') && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-[#ccff00]/70" /> {user.email}</span>}
          {user.dni && <span className="flex items-center gap-1 font-mono tracking-normal"><CreditCard className="w-3 h-3 text-[#ccff00]/70" /> {user.dni}</span>}
          {user.job_title && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-[#ccff00]/70" /> {user.job_title}</span>}
        </div>
      </div>
      
      <div className="flex items-center gap-2 mt-2 sm:mt-0 relative z-10">
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(); }}
          className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-[#00FFFF]/20 hover:border-[#00FFFF]/40 text-white/50 hover:text-[#00FFFF] transition-colors cursor-pointer"
          title="Editar Operario"
        >
          <UserCog className="w-4 h-4 pointer-events-none" />
        </button>

        <button onClick={onToggleCode} disabled={loadingCode}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
            visible && code ? 'bg-[#00FFFF]/10 text-[#00FFFF] border border-[#00FFFF]/30 hover:bg-[#00FFFF]/20' : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white'
          }`}>
          {loadingCode ? <Loader2 className="w-3.5 h-3.5 animate-spin pointer-events-none" /> : visible ? <EyeOff className="w-3.5 h-3.5 pointer-events-none" /> : <Eye className="w-3.5 h-3.5 pointer-events-none" />}
          {visible && code ? <span className="font-mono tracking-[0.1em] text-lg leading-none pt-0.5">{code}</span> : <span>Credencial</span>}
        </button>
        
        {visible && code && (
          <button onClick={(e) => onCopy(code, e)} className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-[#ccff00]/20 hover:border-[#ccff00]/40 text-white/50 hover:text-[#ccff00] transition-colors">
            {copied === user.id ? <Check className="w-4 h-4 text-[#ccff00] pointer-events-none" /> : <Copy className="w-4 h-4 pointer-events-none" />}
          </button>
        )}
        
        <button onClick={onRegenerate} className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-orange-500/20 hover:border-orange-500/40 text-white/50 hover:text-orange-400 transition-colors" title="Regenerar clave">
          <RefreshCw className="w-4 h-4 pointer-events-none" />
        </button>

        <button 
          onClick={(e) => { e.stopPropagation(); onSendCode(); }}
          className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-blue-500/20 hover:border-blue-500/40 text-white/50 hover:text-blue-400 transition-colors" 
          title="Enviar código por email"
        >
          <Mail className="w-4 h-4 pointer-events-none" />
        </button>

        {user.role === 'admin' && adminCode ? (
          <button
            onClick={() => { navigator.clipboard.writeText(adminCode); toast.success('Código admin copiado'); }}
            className="p-2.5 rounded-lg bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] hover:bg-[#ccff00]/20 transition-colors"
            title={`Código admin: ${adminCode}`}
          >
            <Shield className="w-4 h-4 pointer-events-none" />
          </button>
        ) : (
          <button
            onClick={onPromote}
            disabled={isPromoting}
            className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-[#ccff00]/20 hover:border-[#ccff00]/30 text-white/30 hover:text-[#ccff00] transition-colors"
            title="Promover a Admin"
          >
            {isPromoting
              ? <Loader2 className="w-4 h-4 animate-spin pointer-events-none" />
              : <Shield className="w-4 h-4 pointer-events-none" />}
          </button>
        )}

        <button onClick={onToggleStatus} disabled={isToggling} className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-orange-500/20 hover:border-orange-500/40 text-white/50 hover:text-orange-400 transition-colors" title={isSuspended ? 'Activar Operario' : 'Suspender Operario'}>
          {isToggling ? <Loader2 className="w-4 h-4 animate-spin pointer-events-none" /> : (isSuspended ? <ToggleLeft className="w-4 h-4 pointer-events-none" /> : <ToggleRight className="w-4 h-4 text-orange-400 pointer-events-none" />)}
        </button>

        <button onClick={onDelete} disabled={isDeleting} className="p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-red-500/20 hover:border-red-500/40 text-white/50 hover:text-red-400 transition-colors" title="Eliminar Registro (LOPD)">
          {isDeleting ? <Loader2 className="w-4 h-4 animate-spin pointer-events-none" /> : <Trash2 className="w-4 h-4 pointer-events-none" />}
        </button>
      </div>
    </div>
  );
};

const orgInputCls = (err) => `cy-input ${err ? 'error' : ''}`;

const OrgField = ({ label, required, error, children }) => (
  <div>
    <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && <p className="text-[10px] text-red-400 uppercase tracking-widest font-black mt-2 flex items-center gap-1.5"><AlertCircle className="w-3 h-3" /> {error}</p>}
  </div>
);

const StatCard = ({ title, value, subText, icon, color }) => (
  <div className="glass-panel p-6 relative group transition-all duration-300 overflow-hidden" style={{ borderColor: `${color}33` }}>
    <div className="absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity" style={{ color }}>
      <div style={{ transform: 'scale(4)' }}>{icon}</div>
    </div>
    <div className="flex items-center gap-3 mb-4" style={{ color }}>
      <div className="p-2 rounded-lg" style={{ background: `${color}22`, border: `1px solid ${color}44` }}>{icon}</div>
      <span className="text-[10px] font-black uppercase tracking-widest">{title}</span>
    </div>
    <div className="text-4xl font-black text-white mb-1 tracking-tight">{value ?? 0}</div>
    <p className="text-[11px] font-bold uppercase tracking-wider text-white/40">{subText}</p>
  </div>
);

const MiniToggle = ({ on, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`relative w-10 h-[22px] rounded-full transition-colors shrink-0 disabled:opacity-50 ${on ? 'bg-[#00FF88]' : 'bg-white/15'}`}
  >
    <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${on ? 'left-[21px]' : 'left-[3px]'}`} />
  </button>
);

/* ── COMPONENTE PRINCIPAL SUPERADMIN ── */

const SuperAdminPanel = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats]        = useState(null);
  const [organizations, setOrgs] = useState([]);
  const [loading, setLoading]    = useState(true);

  const [activeOrg, setActiveOrg]       = useState(null);
  const [orgUsers, setOrgUsers]         = useState([]);
  const [orgDepts, setOrgDepts]         = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [usersTab, setUsersTab]         = useState('list');
  const [deletingUser, setDeletingUser] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);

  const [showOrgModal, setShowOrgModal] = useState(false);
  const [orgForm, setOrgForm]           = useState(EMPTY_ORG);
  const [orgErrors, setOrgErrors]       = useState({});
  const [savingOrg, setSavingOrg]       = useState(false);

  const [userForm, setUserForm]       = useState(EMPTY_USER);
  const [userErrors, setUserErrors]   = useState({});
  const [savingUser, setSavingUser]   = useState(false);
  const [newUserCode, setNewUserCode] = useState(null);

  const [visibleCodes, setVisibleCodes] = useState({});
  const [codes, setCodes]               = useState({});
  const [loadingCode, setLoadingCode]   = useState({});
  const [copied, setCopied]             = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser]     = useState(null);
  const [editForm, setEditForm]           = useState({});
  const [savingEdit, setSavingEdit]       = useState(false);


  const [adminCodes, setAdminCodes]   = useState({});
  const [promotingUser, setPromotingUser] = useState(null);



  const visibleUsers = orgUsers.filter(u => {
    const isDeletedName = u.name === '[Usuario eliminado]';
    const isDeletedEmail = (u.email || '').toLowerCase().includes('deleted.local');
    const isDeletedStatus = u.status === 'deleted';
    
    return !isDeletedName && !isDeletedEmail && !isDeletedStatus;
  });

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name:            user.name            || '',
      email:           user.email           || '',
      dni:             user.dni             || '',
      social_security: user.social_security || '',
      job_title:       user.job_title       || '',
      department:      user.department      || '',
    });
    setShowEditModal(true);
  };

  const saveEditUser = async () => {
    if (!editForm.name.trim()) return;
    setSavingEdit(true);
    try {
      await api.patch(`/admin/users/${editingUser.id}`, editForm);
      toast.success('Empleado actualizado');
      setShowEditModal(false);
      const u = await api.get(`/admin/users?organization_id=${activeOrg.id}`);
      setOrgUsers(u.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingEdit(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [s, o] = await Promise.all([
        api.get('/superadmin/stats'),
        api.get('/superadmin/organizations'),
      ]);
      setStats(s.data);
      setOrgs(o.data);
    } catch (err) {
      toast.error('Error al cargar datos: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const refreshActiveOrg = async () => {
  const o = await api.get('/superadmin/organizations');
  setOrgs(o.data);
  setActiveOrg(prev => {
    const fresh = o.data.find(x => x.id === prev?.id) || prev;
    return fresh;
  });
};
  const loadOrgModules = async (orgId) => {
  setLoadingMods(true);
  try {
    const res = await api.get(`/superadmin/organizations/${orgId}/modules`);
    setOrgModules(res.data || []);
  } catch {
    toast.error('Error al cargar módulos');
    setOrgModules([]);
  } finally {
    setLoadingMods(false);
  }
};

const patchLocalModule = (id, patch) =>
  setOrgModules(prev => prev.map(m => (m.id === id ? { ...m, ...patch } : m)));

const updateModSetting = async (m, field, value) => {
  const key = `${m.id}-${field}`;
  setSavingMod(p => ({ ...p, [key]: true }));
  try {
    await api.put(`/admin/modules/${m.id}/settings`, { [field]: value });
    const stored = field === 'require_screen_recording' ? 'requires_screen_record' : field;
    patchLocalModule(m.id, { [stored]: value });
    toast.success('Configuración guardada');
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Error al guardar');
  } finally {
    setSavingMod(p => ({ ...p, [key]: false }));
  }
};

const generateRoleplay = async (m) => {
  setRoleplayBusy(m.id);
  try {
    const res = await api.post(`/admin/modules/${m.id}/generate-roleplay`);
    patchLocalModule(m.id, { roleplay_scenario: res.data });
    toast.success('Juego de rol activado');
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Error al generar el roleplay');
  } finally {
    setRoleplayBusy(null);
  }
};

const removeRoleplay = async (m) => {
  if (!confirm(`¿Quitar el juego de rol de "${m.title}"?`)) return;
  setRoleplayBusy(m.id);
  try {
    await api.delete(`/admin/modules/${m.id}/roleplay`);
    patchLocalModule(m.id, { roleplay_scenario: null });
    toast.success('Juego de rol desactivado');
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Error al quitar el roleplay');
  } finally {
    setRoleplayBusy(null);
  }
};
  const openOrg = async (org) => {
    setActiveOrg(org);
    setOrgTab('users');            
    loadOrgModules(org.id); 
    setUsersTab('list');
    setNewUserCode(null);
    setUserForm(EMPTY_USER);
    setLoadingUsers(true);
    try {
      const [u, d] = await Promise.all([
        api.get(`/admin/users?organization_id=${org.id}`),
        api.get(`/admin/departments?organization_id=${org.id}`),
      ]);
      setOrgUsers(u.data);
      setOrgDepts(d.data.filter(dep => dep.organization_id === org.id || !dep.organization_id));
    } catch {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const closeOrg = () => {
    setActiveOrg(null); setOrgUsers([]); setOrgDepts([]);
    setOrgModules([]); setOrgTab('users');
    setUsersTab('list'); setNewUserCode(null); setUserForm(EMPTY_USER);
    setUserErrors({}); setVisibleCodes({}); setCodes({});
  };

  const toggleOrg = async (orgId, e) => {
    e.stopPropagation();
    try {
      await api.put(`/superadmin/organizations/${orgId}/toggle`);
      toast.success('Estado actualizado');
      loadData();
      if (activeOrg?.id === orgId) setActiveOrg(prev => ({ ...prev, active: !prev.active }));
    } catch (err) { toast.error('Error: ' + err.message); }
  };

  const validateOrg = () => {
    const e = {};
    if (!orgForm.name.trim()) e.name = 'El nombre es obligatorio';
    if (orgForm.contact_email && !EMAIL_RE.test(orgForm.contact_email)) e.contact_email = 'Email inválido';
    if (Number(orgForm.max_users) < 1) e.max_users = 'Mínimo 1 usuario';
    setOrgErrors(e);
    return Object.keys(e).length === 0;
  };

  const promoteToAdmin = async (userId, userName) => {
    if (!confirm(`¿Convertir a "${userName}" en Admin? Recibirá un código de admin separado.`)) return;
    setPromotingUser(userId);
    try {
      const res = await api.post(`/superadmin/users/${userId}/assign-admin-role`);
      setAdminCodes(p => ({ ...p, [userId]: res.data.admin_code }));
      toast.success(`✅ Código admin: ${res.data.admin_code}`);
      const u = await api.get(`/admin/users?organization_id=${activeOrg.id}`);
      setOrgUsers(u.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al promover');
    } finally {
      setPromotingUser(null);
    }
  };

  const handleCreateOrg = async () => {
    if (!validateOrg()) return;
    setSavingOrg(true);
    try {
      await api.post('/organizations', {
        name: orgForm.name.trim(),
        description: orgForm.description.trim() || null,
        contact_email: orgForm.contact_email.trim() || null,
        logo_url: orgForm.logo_url.trim() || null,
        bg_image_url: orgForm.bg_image_url?.trim() || null,
        max_users: Number(orgForm.max_users),
        features: { modules: true, skill_paths: true, ai_assistant: true, analytics: true, avatar_call: false, roleplay: false },
      });
      toast.success(`Corporación "${orgForm.name}" registrada`);
      setShowOrgModal(false);
      setOrgForm(EMPTY_ORG);
      setOrgErrors({});
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en el registro');
    } finally {
      setSavingOrg(false);
    }
  };

  const validateUser = () => {
    const e = {};
    if (!userForm.name.trim())            e.name            = 'Requerido';
    if (!userForm.dni.trim())             e.dni             = 'Requerido';
    if (!userForm.social_security.trim()) e.social_security = 'Requerido';
    if (userForm.email && !EMAIL_RE.test(userForm.email)) e.email = 'Email inválido';
    return Object.keys(e).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateUser()) return;
    setSavingUser(true);
    try {
      const res = await api.post('/superadmin/users/create-with-code', {
        name: userForm.name.trim(), organization_id: activeOrg.id,
        role: userForm.role,
        learning_style: userForm.learning_style,
        department: userForm.department || null, email: userForm.email.trim() || null,
        dni: userForm.dni.trim(), social_security: userForm.social_security.trim(),
        job_title: userForm.job_title.trim() || null,
      });
      setNewUserCode(res.data.access_code);
      toast.success(`Operario añadido`);
      const u = await api.get(`/admin/users?organization_id=${activeOrg.id}`);
      setOrgUsers(u.data);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fallo de inserción');
    } finally { setSavingUser(false); }
  };

  const resetUserForm = () => { setUserForm(EMPTY_USER); setUserErrors({}); setNewUserCode(null); setUsersTab('list'); };

  const fetchCode = async (userId) => {
    if (codes[userId]) { setVisibleCodes(p => ({ ...p, [userId]: !p[userId] })); return; }
    setLoadingCode(p => ({ ...p, [userId]: true }));
    try {
      const res = await api.get(`/superadmin/users/${userId}/access-code`);
      setCodes(p => ({ ...p, [userId]: res.data.access_code }));
      setVisibleCodes(p => ({ ...p, [userId]: true }));
    } catch { toast.error('Imposible desencriptar clave'); }
    finally { setLoadingCode(p => ({ ...p, [userId]: false })); }
  };

  const regenerateCode = async (userId, userName, e) => {
    e.stopPropagation();
    if (!confirm(`¿Regenerar clave de acceso para "${userName}"? La credencial anterior será invalidada.`)) return;
    try {
      const res = await api.post(`/superadmin/users/${userId}/regenerate-code`);
      setCodes(p => ({ ...p, [userId]: res.data.access_code }));
      setVisibleCodes(p => ({ ...p, [userId]: true }));
      toast.success('Clave regenerada');
    } catch { toast.error('Error de regeneración'); }
  };

  const copyCode = (code, key, e) => {
    e?.stopPropagation();
    navigator.clipboard.writeText(code);
    setCopied(key); toast.success('Clave copiada');
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleUser = async (userId, currentStatus, e) => {
    e?.stopPropagation();
    setTogglingUser(userId);
    try {
      await api.put(`/superadmin/users/${userId}/toggle-status`);
      const u = await api.get(`/admin/users?organization_id=${activeOrg.id}`);
      setOrgUsers(u.data);
      toast.success('Estado actualizado');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setTogglingUser(null);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!confirm(`¿Eliminar a "${userName}"? Se mantendrá registro LOPD.`)) return;
    setDeletingUser(userId);
    try {
      await api.delete(`/superadmin/users/${userId}`);
      const u = await api.get(`/admin/users?organization_id=${activeOrg.id}`);
      setOrgUsers(u.data);
      toast.success(`Usuario eliminado.`);
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setDeletingUser(null);
    }
  };

  const sendCodeToUser = async (userId, userName, userEmail) => {
    if (!userEmail || userEmail.includes('@noemail.local')) {
      toast.error(`${userName} no tiene email registrado`);
      return;
    }
    if (!confirm(`¿Enviar código de acceso a ${userEmail}?`)) return;
    try {
      await api.post(`/admin/users/${userId}/send-code`);
      toast.success(`Código enviado a ${userEmail}`);
    } catch (err) {
      toast.error('Error al enviar: ' + (err.response?.data?.detail || err.message));
    }
  };

  const setUF = (key, val) => { setUserForm(p => ({ ...p, [key]: val })); setUserErrors(p => ({ ...p, [key]: '' })); };
  const ErrMsg = ({ msg }) => msg ? <p className="text-[10px] uppercase font-black tracking-widest text-red-400 mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" /> {msg}</p> : null;
  const [orgTab, setOrgTab]             = useState('users');   // 'users' | 'modules'
  const [orgModules, setOrgModules]     = useState([]);
  const [loadingMods, setLoadingMods]   = useState(false);
  const [savingMod, setSavingMod]       = useState({});
  const [roleplayBusy, setRoleplayBusy] = useState(null);
  if (loading) {
    return (
      <div className="dash-root flex items-center justify-center">
        <style>{GLOBAL_STYLES}</style>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-[#00FFFF] drop-shadow-[0_0_15px_#00FFFF]" />
          <p className="text-[#00FFFF] text-[10px] uppercase tracking-widest font-black">Conectando...</p>
        </div>
      </div>
    );
  }


  return (
    <>
      <div className="dash-root">
        <style>{GLOBAL_STYLES}</style>
        <div className="main-content">

          {activeOrg ? (
            <div className="flex flex-col lg:flex-row gap-6 h-full items-start">
              
              {/* ── SIDEBAR (Empresas) ── */}
              <div className="w-full lg:w-80 shrink-0 space-y-4 lg:sticky lg:top-8">
                <button onClick={closeOrg} className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-[#00FFFF] hover:text-white mb-2 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Volver a Global
                </button>
                
                <div className="space-y-3">
                  {organizations.map(org => (
                    <button key={org.id} type="button" onClick={() => openOrg(org)}
                      aria-current={activeOrg.id === org.id ? 'true' : undefined}
                      className={`block w-full text-left p-4 rounded-xl border cursor-pointer transition-all ${
                        activeOrg.id === org.id 
                          ? 'bg-[#00FFFF]/10 border-[#00FFFF]/40 shadow-[0_0_15px_rgba(0,255,255,0.1)]' 
                          : 'bg-black/40 border-white/5 hover:border-white/20'
                      }`}>
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${activeOrg.id === org.id ? 'bg-[#00FFFF]/20 text-[#00FFFF]' : 'bg-white/5 text-white/40'}`}>
                          <Building2 className="w-5 h-5 shrink-0" />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-[13px] font-bold truncate ${activeOrg.id === org.id ? 'text-[#00FFFF]' : 'text-white'}`}>{org.name}</p>
                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30 mt-0.5">{org.current_users} Operarios</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── MAIN AREA (Detalle de Empresa) ── */}
              <div className="flex-1 min-w-0 space-y-6 w-full">
                
                {/* Header Empresa */}
                <div className="glass-panel p-6 glow-cyan flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="p-4 bg-[#00FFFF]/10 border border-[#00FFFF]/30 rounded-2xl text-[#00FFFF] shrink-0">
  {activeOrg.logo_url
    ? <img src={activeOrg.logo_url} alt="" className="w-8 h-8 object-contain" />
    : <Building2 className="w-8 h-8" />}
</div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">{activeOrg.name}</h2>
                        <span className={`text-[9px] px-2.5 py-1 rounded font-black uppercase tracking-widest border ${activeOrg.active ? 'bg-[#ccff00]/10 text-[#ccff00] border-[#ccff00]/30' : 'bg-red-500/10 text-red-500 border-red-500/30'}`}>
                          {activeOrg.active ? 'Operativa' : 'Suspendida'}
                        </span>
                      </div>
                      <p className="text-white/50 text-[13px]">{activeOrg.description || 'Sin información adicional registrada.'}</p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-center">
                    <div className="flex gap-3">
                      {[{ label: 'Usuarios', value: `${visibleUsers.length}/${activeOrg.max_users}`, icon: Users, color: '#00FFFF' }, { label: 'Módulos', value: activeOrg.total_modules ?? 0, icon: BookOpen, color: '#ccff00' }].map(({ label, value, icon: Icon, color }) => (
                        <div key={label} className="text-center px-4 py-3 bg-black/40 border border-white/10 rounded-xl">
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <Icon className="w-3.5 h-3.5" style={{ color }} />
                            <p className="text-[10px] uppercase font-black tracking-widest text-white/50">{label}</p>
                          </div>
                          <p className="text-lg font-black text-white">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                    <div className="flex gap-2 border-b border-white/10">
  {[
    { id: 'users',   label: 'Operarios', icon: Users },
    { id: 'modules', label: `Módulos (${orgModules.length})`, icon: BookOpen },
    { id: 'branding', label: 'Identidad', icon: Palette },
    { id: 'plus',     label: 'Plan Plus', icon: Sparkles },  
  ].map(t => (
    <button key={t.id} onClick={() => setOrgTab(t.id)}
      className={`flex items-center gap-2 px-5 py-3 text-[11px] font-black uppercase tracking-widest border-b-2 transition-colors ${
        orgTab === t.id ? 'border-[#00FFFF] text-[#00FFFF]' : 'border-transparent text-white/40 hover:text-white'
      }`}>
      <t.icon className="w-4 h-4" /> {t.label}
    </button>
  ))}
</div>
                {/* Panel de Usuarios */}
                {orgTab === 'users' && (
                <div className="glass-panel overflow-hidden">
                  <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/[0.02]">
                    <h3 className="font-black text-white text-[15px] uppercase tracking-widest flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#ccff00]" /> Base de Datos de Operarios
                      <span className="text-[10px] px-2.5 py-1 bg-white/10 text-white/60 rounded-md border border-white/10">{visibleUsers.length}</span>
                    </h3>
                    
                    {usersTab === 'list' && (
                      <div className="flex gap-2">
                        <button onClick={() => setUsersTab('bulk')}
                          className="flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 hover:bg-white/10 rounded-xl text-[11px] font-black uppercase tracking-widest transition-colors">
                          <FileSpreadsheet className="w-4 h-4" /> Carga Masiva
                        </button>
                        <button onClick={() => setUsersTab('create')}
                          className="flex items-center gap-2 px-5 py-2.5 btn-neon-lime rounded-xl text-[11px] font-black uppercase tracking-widest">
                          <UserPlus className="w-4 h-4" /> Añadir Operario
                        </button>
                      </div>
                    )}

                    {usersTab !== 'list' && (
                      <button onClick={() => setUsersTab('list')}
                        className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 rounded-xl text-[11px] font-black uppercase tracking-widest text-white/60 hover:text-white transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Volver a la Lista
                      </button>
                    )}
                  </div>

                  {/* VISTA DE LISTA DE USUARIOS */}
                  {usersTab === 'list' && (
                    <div className="divide-y divide-white/5">
                      {loadingUsers ? (
                        <div className="p-12 text-center text-white/40">
                          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#00FFFF]" />
                          Cargando operarios...
                        </div>
                      ) : visibleUsers.length === 0 ? (
                        <div className="p-12 text-center text-white/40">No hay operarios registrados en esta organización.</div>
                      ) : (
                        visibleUsers.map((user) => (
                          <UserRow
                            key={user.id}
                            user={user}
                            code={codes[user.id]}
                            visible={visibleCodes[user.id]}
                            loadingCode={loadingCode[user.id]}
                            copied={copied}
                            departments={orgDepts}
                            isToggling={togglingUser === user.id}
                            isDeleting={deletingUser === user.id}
                            onToggleCode={() => fetchCode(user.id)}
                            onCopy={copyCode}
                            onRegenerate={(e) => regenerateCode(user.id, user.name, e)}
                            onToggleStatus={(e) => toggleUser(user.id, user.status, e)}
                            onDelete={() => deleteUser(user.id, user.name)}
                            onEdit={() => openEditUser(user)}
                            onSendCode={() => sendCodeToUser(user.id, user.name, user.email)}
                            adminCode={adminCodes[user.id]}
                            isPromoting={promotingUser === user.id}
                            onPromote={() => promoteToAdmin(user.id, user.name)}
                          />
                        ))
                      )}
                    </div>
                  )}

                  {/* VISTA CARGA MASIVA */}
                  {usersTab === 'bulk' && (
                    <div className="p-6">
                      <BulkUserUpload 
                        organizationId={activeOrg.id} 
                        onSuccess={() => {
                          openOrg(activeOrg);
                          setUsersTab('list');
                        }} 
                      />
                    </div>
                  )}

                  {/* VISTA CREAR OPERARIO */}
                  {usersTab === 'create' && (
                    <div className="p-6 max-w-2xl">
                      {newUserCode ? (
                        <div className="p-6 bg-[#00FFFF]/10 border border-[#00FFFF]/30 rounded-2xl text-center space-y-4">
                          <CheckCircle2 className="w-12 h-12 text-[#00FFFF] mx-auto" />
                          <h4 className="text-xl font-black text-white">¡Operario Registrado!</h4>
                          <p className="text-xs text-white/60">Guarde la siguiente clave de acceso único para el operario:</p>
                          <div className="p-4 bg-black/60 rounded-xl border border-[#00FFFF]/40 flex items-center justify-between">
                            <span className="font-mono text-2xl font-black text-[#00FFFF] tracking-widest">{newUserCode}</span>
                            <button onClick={() => copyCode(newUserCode, 'new')} className="p-2 btn-neon-cyan rounded-lg">
                              {copied === 'new' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                            </button>
                          </div>
                          <button onClick={resetUserForm} className="w-full py-3 btn-neon-lime rounded-xl text-xs font-black uppercase tracking-widest">
                            Registrar Otro Operario
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Nombre Completo *</label>
                              <input type="text" value={userForm.name} onChange={(e) => setUF('name', e.target.value)} className={orgInputCls(userErrors.name)} placeholder="Ej: Juan Pérez" />
                              <ErrMsg msg={userErrors.name} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">DNI / NIE *</label>
                              <input type="text" value={userForm.dni} onChange={(e) => setUF('dni', e.target.value)} className={orgInputCls(userErrors.dni)} placeholder="12345678X" />
                              <ErrMsg msg={userErrors.dni} />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Nº Seguridad Social *</label>
                              <input type="text" value={userForm.social_security} onChange={(e) => setUF('social_security', e.target.value)} className={orgInputCls(userErrors.social_security)} placeholder="281234567890" />
                              <ErrMsg msg={userErrors.social_security} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Email (Opcional)</label>
                              <input type="email" value={userForm.email} onChange={(e) => setUF('email', e.target.value)} className={orgInputCls(userErrors.email)} placeholder="correo@empresa.com" />
                              <ErrMsg msg={userErrors.email} />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Puesto de Trabajo</label>
                              <input type="text" value={userForm.job_title} onChange={(e) => setUF('job_title', e.target.value)} className="cy-input" placeholder="Técnico de Mantenimiento" />
                            </div>
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Departamento</label>
                              <select value={userForm.department} onChange={(e) => setUF('department', e.target.value)} className="cy-input bg-black">
                                <option value="">Seleccionar Departamento</option>
                                {orgDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                              </select>
                            </div>
                          </div>

                          <div className="flex justify-end gap-3 pt-4">
                            <button onClick={() => setUsersTab('list')} className="px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-white/60 hover:text-white">Cancelar</button>
                            <button onClick={handleCreateUser} disabled={savingUser} className="btn-neon-lime px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2">
                              {savingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Guardar Operario
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
                )}
                {orgTab === 'modules' && (
  <div className="glass-panel overflow-hidden">
    <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/[0.02]">
      <h3 className="font-black text-white text-[15px] uppercase tracking-widest flex items-center gap-3">
        <BookOpen className="w-5 h-5 text-[#ccff00]" /> Módulos de la empresa
      </h3>
      <button
        onClick={() => navigate(`/superadmin/modules?org=${activeOrg.id}`)}
        className="px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 rounded-xl text-[11px] font-black uppercase tracking-widest text-white/60 hover:text-white transition-colors">
        Asignar / eliminar en repositorio
      </button>
    </div>

    {loadingMods ? (
      <div className="p-12 text-center text-white/40">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#00FFFF]" /> Cargando módulos...
      </div>
    ) : orgModules.length === 0 ? (
      <div className="p-12 text-center text-white/40">Esta empresa no tiene módulos todavía.</div>
    ) : (
      <div className="divide-y divide-white/5">
        {orgModules.map(m => {
          const recOn  = m.requires_screen_record === true;
          const timeOn = m.require_time_tracking === true;
          const dipOn  = m.diploma_enabled === true;
          const hasRP  = !!m.roleplay_scenario;
          const busy   = roleplayBusy === m.id;
          return (
            <div key={m.id} className="p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-black text-white text-[15px] mr-2">{m.title}</h4>
                {m.category && <span className="px-2 py-0.5 bg-[#00FFFF]/10 border border-[#00FFFF]/30 text-[#00FFFF] text-[9px] uppercase font-black tracking-widest rounded-md">{m.category}</span>}
                {m.has_submodules && <span className="px-2 py-0.5 bg-[#FF00FF]/10 border border-[#FF00FF]/30 text-[#FF00FF] text-[9px] uppercase font-black tracking-widest rounded-md">{m.submodule_count || '?'} unidades</span>}
                {hasRP && <span className="px-2 py-0.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] text-[9px] uppercase font-black tracking-widest rounded-md">Juego de rol</span>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Grabación */}
                <div className="flex items-center justify-between gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-white/70"><Video className="w-4 h-4" /> Grabación pantalla</span>
                  <MiniToggle on={recOn} disabled={savingMod[`${m.id}-require_screen_recording`]}
                    onClick={() => updateModSetting(m, 'require_screen_recording', !recOn)} />
                </div>

                {/* Tiempo mínimo */}
                <div className="flex items-center justify-between gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-white/70"><Clock className="w-4 h-4" /> Tiempo mínimo</span>
                  <div className="flex items-center gap-2">
                    {timeOn && (
                      <>
                        <input type="number" min="1" max="999" defaultValue={m.hours || 1}
                          onBlur={e => updateModSetting(m, 'hours', Number(e.target.value))}
                          className="w-14 px-2 py-1 rounded-md bg-black/40 border border-white/20 text-center text-xs font-bold text-[#ccff00]" />
                        <span className="text-[10px] text-white/40">h</span>
                      </>
                    )}
                    <MiniToggle on={timeOn} disabled={savingMod[`${m.id}-require_time_tracking`]}
                      onClick={() => updateModSetting(m, 'require_time_tracking', !timeOn)} />
                  </div>
                </div>

                {/* Diploma */}
                <div className="flex items-center justify-between gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-white/70"><Award className="w-4 h-4" /> Diploma al completar</span>
                  <MiniToggle on={dipOn} disabled={savingMod[`${m.id}-diploma_enabled`]}
                    onClick={() => updateModSetting(m, 'diploma_enabled', !dipOn)} />
                </div>

                {/* Juego de rol */}
<div className="flex items-center justify-between gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
  <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-white/70"><Brain className="w-4 h-4" /> Juego de rol</span>
  {activeOrg.features?.roleplay ? (
    <div className="flex items-center gap-2">
      <button onClick={() => generateRoleplay(m)} disabled={busy}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] hover:bg-[#ccff00]/20 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {hasRP ? 'Regenerar' : 'Activar'}
      </button>
      {hasRP && (
        <button onClick={() => removeRoleplay(m)} disabled={busy}
          className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 disabled:opacity-50" title="Quitar juego de rol">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  ) : (
    <span className="text-[10px] font-black uppercase tracking-widest text-white/30">Requiere Plan Plus</span>
  )}
</div>
             
              </div>

              {hasRP && (
                <details className="p-3 bg-black/30 border border-white/10 rounded-xl">
                  <summary className="cursor-pointer text-[11px] font-black uppercase tracking-widest text-[#ccff00]">
                    {m.roleplay_scenario.title} · {m.roleplay_scenario.steps?.length || 0} pasos
                  </summary>
                  <p className="text-xs text-white/50 mt-2">{m.roleplay_scenario.description}</p>
                  <ol className="mt-2 space-y-1.5 list-decimal list-inside text-xs text-white/70">
                    {(m.roleplay_scenario.steps || []).map(s => <li key={s.id}>{s.situation}</li>)}
                  </ol>
                </details>
              )}
            </div>
          );
        })}
      </div>
    )}
    

  </div>
)}
{orgTab === 'branding' && (
  <div className="glass-panel p-6">
    <OrgBrandingEditor key={activeOrg.id} org={activeOrg} onSaved={refreshActiveOrg} />
  </div>
)}
{orgTab === 'plus' && (
  <div className="glass-panel p-6">
    <OrgPlusFeatures key={activeOrg.id} org={activeOrg} users={visibleUsers} onSaved={refreshActiveOrg} />
  </div>
)}
              </div>
                  
            </div>
          ) : (
            
            /* ── VISTA GLOBAL (Listado de Organizaciones) ── */
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-black title-neon">Panel Superadmin</h1>
                  <p className="text-white/50 text-xs mt-1 uppercase tracking-widest font-bold">Gestión Global de Corporaciones y Operarios</p>
                </div>
                <button onClick={() => setShowOrgModal(true)} className="btn-neon-cyan px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 self-start md:self-auto">
                  <Plus className="w-4 h-4" /> Nueva Corporación
                </button>
              </div>

              {/* MÉTICAS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Corporaciones" value={stats?.total_organizations} subText="Registradas" icon={<Building2 className="w-5 h-5" />} color="#00FFFF" />
                <StatCard title="Total Operarios" value={stats?.total_users} subText="En plataforma" icon={<Users className="w-5 h-5" />} color="#ccff00" />
                <StatCard title="Módulos" value={stats?.total_modules} subText="Activos" icon={<BookOpen className="w-5 h-5" />} color="#38bdf8" />
                <StatCard title="Cursos" value={stats?.total_courses} subText="Completados" icon={<TrendingUp className="w-5 h-5" />} color="#a855f7" />
              </div>

              {/* LISTA DE CORPORACIONES */}
              <div className="glass-panel p-6">
                <h3 className="text-lg font-black text-white mb-6 uppercase tracking-wider flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#00FFFF]" /> Corporaciones Registradas
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {organizations.map(org => (
                    <div key={org.id} onClick={() => openOrg(org)} className="p-5 rounded-2xl bg-black/40 border border-white/10 hover:border-[#00FFFF]/40 transition-all cursor-pointer group flex flex-col justify-between space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-[#00FFFF]/10 border border-[#00FFFF]/30 flex items-center justify-center text-[#00FFFF]">
                            <Building2 className="w-5 h-5" />
                          </div>
                          <span className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-widest border ${org.active ? 'bg-[#ccff00]/10 text-[#ccff00] border-[#ccff00]/30' : 'bg-red-500/10 text-red-500 border-red-500/30'}`}>
                            {org.active ? 'Activa' : 'Inactiva'}
                          </span>
                        </div>
                        <h4 className="font-bold text-white group-hover:text-[#00FFFF] transition-colors">{org.name}</h4>
                        <p className="text-xs text-white/40 line-clamp-2 mt-1">{org.description || 'Sin descripción'}</p>
                      </div>

                      <div className="pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-bold text-white/60">
                        <span>{org.current_users} / {org.max_users} usuarios</span>
                        <button onClick={(e) => toggleOrg(org.id, e)} className="text-xs text-white/40 hover:text-white p-1" title="Cambiar Estado">
                          <Power className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── MODAL: NUEVA ORGANIZACIÓN ── */}
      {showOrgModal && (
        <ModalOverlay onClose={() => setShowOrgModal(false)}>
<div className="modal-superadmin p-6 rounded-xl shadow-2xl max-w-2xl w-full border border-white/10">            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-xl font-black text-white">Nueva Corporación</h3>
              <button onClick={() => setShowOrgModal(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <OrgField label="Nombre" required error={orgErrors.name}>
              <input type="text" value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} className={orgInputCls(orgErrors.name)} placeholder="Nombre de la empresa" />
            </OrgField>

            <OrgField label="Descripción">
              <textarea value={orgForm.description} onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })} className="cy-input min-h-[80px]" placeholder="Breve descripción" />
            </OrgField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <OrgField label="Email de Contacto" error={orgErrors.contact_email}>
                <input type="email" value={orgForm.contact_email} onChange={(e) => setOrgForm({ ...orgForm, contact_email: e.target.value })} className={orgInputCls(orgErrors.contact_email)} placeholder="admin@empresa.com" />
              </OrgField>

              <OrgField label="Máximo de Usuarios" required error={orgErrors.max_users}>
                <input type="number" value={orgForm.max_users} onChange={(e) => setOrgForm({ ...orgForm, max_users: e.target.value })} className={orgInputCls(orgErrors.max_users)} min="1" />
              </OrgField>
            </div>

            <OrgField label="URL del Logo (Opcional)">
              <input type="text" value={orgForm.logo_url} onChange={(e) => setOrgForm({ ...orgForm, logo_url: e.target.value })} className="cy-input" placeholder="https://..." />
            </OrgField>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button onClick={() => setShowOrgModal(false)} className="px-4 py-2 text-xs font-black uppercase text-white/60 hover:text-white">Cancelar</button>
              <button onClick={handleCreateOrg} disabled={savingOrg} className="btn-neon-cyan px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2">
                {savingOrg ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Organización'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── MODAL: EDITAR USUARIO ── */}
      {showEditModal && editingUser && (
        <ModalOverlay onClose={() => setShowEditModal(false)}>
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-xl font-black text-white">Editar Operario</h3>
              <button onClick={() => setShowEditModal(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Nombre Completo</label>
              <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="cy-input" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="cy-input" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">DNI / NIE</label>
                <input type="text" value={editForm.dni} onChange={(e) => setEditForm({ ...editForm, dni: e.target.value })} className="cy-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Seguridad Social</label>
                <input type="text" value={editForm.social_security} onChange={(e) => setEditForm({ ...editForm, social_security: e.target.value })} className="cy-input" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Puesto de Trabajo</label>
                <input type="text" value={editForm.job_title} onChange={(e) => setEditForm({ ...editForm, job_title: e.target.value })} className="cy-input" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-[#00FFFF] mb-2">Departamento</label>
              <select value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="cy-input bg-black">
                <option value="">Seleccionar Departamento</option>
                {orgDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-xs font-black uppercase text-white/60 hover:text-white">Cancelar</button>
              <button onClick={saveEditUser} disabled={savingEdit} className="btn-neon-lime px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2">
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
};

export default SuperAdminPanel;